// V1 source: customer/midas/components/MidasPhoneArtifactSheet.tsx
// Strategy: scratch
// Verified no-MUI: yes
//
// V2 phone full-screen artifact takeover. Replaces V1's bottom-sheet with a
// full-screen Dialog overlay (Sheet → Screen rename, per Phase 2B rollout
// note). When the user taps an artifact on phone (≤768px), the takeover
// covers the chat with:
//   - Top bar: [← Back to chat] left, truncated artifact title center,
//     dock-close X right.
//   - Tab strip below: horizontally scrollable, +N chevron dropdown for
//     overflow tabs (reuses the same `selectVisibleArtifactTabs` /
//     `selectOverflowArtifactTabs` selectors as the desktop dock).
//   - Body: REUSES `MidasArtifactBodyV2` so viewer-mounting logic stays in
//     one place (image / diagram_overlay / action_form). Body padding=0 so
//     the viewer chrome reaches the screen edge.
//
// "Back to chat" closes the panel via `closeArtifactPanel` — the artifact
// stays in `state.artifactTabs`, so re-tapping the artifact in chat returns
// to this takeover. The dock-close X removes the active tab via
// `closeArtifactTab` (matches V1 dock semantics).
//
// shadcn Dialog primitive provides the full-screen overlay + Escape close +
// focus trap. We mount it from `MidasPageV2` (not from inside the chat
// thread tree) so the Radix Portal reliably renders above any chat-area
// scrollers.

import React from "react";
import { ChevronLeft, ChevronDown, X } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  closeArtifactPanel,
  closeArtifactTab,
  selectOverflowArtifactTabs,
  selectVisibleArtifactTabs,
  setActiveArtifactTab,
} from "../../midasPageSlice";
import { Dialog, DialogContent } from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { MidasArtifactBodyV2 } from "./MidasArtifactPanelV2";

// Title truncation — same constant as the desktop header
const MAX_PHONE_TITLE_LENGTH = 28;
const truncateTitle = (title: string): string => {
  if (title.length <= MAX_PHONE_TITLE_LENGTH) return title;
  return title.slice(0, MAX_PHONE_TITLE_LENGTH - 1) + "…";
};

const MidasPhoneArtifactScreenV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");

  const visibleTabs = useAppSelector(selectVisibleArtifactTabs);
  const overflowTabs = useAppSelector(selectOverflowArtifactTabs);
  const allTabs = useAppSelector((state) => state.midasPage.artifactTabs);
  const activeTabId = useAppSelector((state) => state.midasPage.activeArtifactTabId);
  const isOpen = useAppSelector((state) => state.midasPage.artifactPanelOpen);

  const activeTab = allTabs.find((t) => t.id === activeTabId);
  const overflowCount = overflowTabs.length;

  const handleBack = () => {
    // Closes the panel but keeps the artifact tab in state so re-tapping the
    // chat surface re-opens this same screen with the same active tab.
    dispatch(closeArtifactPanel());
  };

  const handleActivate = (tabId: string) => {
    dispatch(setActiveArtifactTab(tabId));
  };

  const handleCloseActiveTab = () => {
    if (activeTabId) {
      dispatch(closeArtifactTab(activeTabId));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleBack(); }}>
      <DialogContent
        aria-label={translate("midas.aria.artifactPanel") || "Artifact viewer"}
        className="bg-midas-bone"
      >
        {/* Top bar: Back · Title · Close-active-tab */}
        <div className="flex h-[52px] flex-shrink-0 items-center gap-2 border-b border-midas-stone bg-midas-bone-light px-2">
          <button
            type="button"
            onClick={handleBack}
            aria-label={translate("midas.artifact.backToChat") || "Back to chat"}
            className="inline-flex h-9 items-center gap-1 rounded-md px-2 font-sora text-[13px] font-semibold text-midas-graphite transition-colors hover:bg-midas-warm-stone/40"
          >
            <ChevronLeft size={18} strokeWidth={2.2} />
            <span className="hidden sm:inline">
              {translate("midas.artifact.backToChat") || "Chat"}
            </span>
          </button>

          <div className="flex flex-1 items-center justify-center px-1">
            <span
              className="truncate font-sora text-[14px] font-semibold text-midas-graphite"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {activeTab ? truncateTitle(activeTab.artifact.title) : ""}
            </span>
          </div>

          <button
            type="button"
            onClick={handleCloseActiveTab}
            aria-label={translate("close")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-midas-graphite transition-colors hover:bg-midas-warm-stone/40 hover:text-midas-terracotta"
          >
            <X size={20} strokeWidth={2.2} />
          </button>
        </div>

        {/* Tab strip — horizontally scrollable with +N overflow dropdown */}
        <div className="flex h-[36px] flex-shrink-0 items-stretch border-b border-midas-stone/60 bg-midas-bone-light">
          <div
            role="tablist"
            aria-label="Artifact tabs"
            className="flex flex-1 items-stretch overflow-x-auto"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {visibleTabs.map((tab) => {
              const isActive = tab.id === activeTabId;
              return (
                <div
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleActivate(tab.id)}
                  className={[
                    "flex flex-shrink-0 cursor-pointer items-center gap-1 px-3 transition-colors duration-150",
                    isActive
                      ? "bg-midas-bone"
                      : "border-r border-midas-stone/60 hover:bg-midas-warm-stone/40",
                  ].join(" ")}
                  style={
                    isActive
                      ? { boxShadow: "inset 0 -2px 0 0 #D4572B" }
                      : undefined
                  }
                >
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="h-[10px] w-[10px] flex-shrink-0 rounded-[2px] bg-midas-terracotta"
                    />
                  )}
                  <span
                    className={[
                      "font-mono text-[11px] tracking-[0.04em]",
                      isActive
                        ? "font-semibold text-midas-graphite"
                        : "font-normal text-midas-warm-graphite",
                    ].join(" ")}
                  >
                    {truncateTitle(tab.artifact.title)}
                  </span>
                </div>
              );
            })}
          </div>

          {overflowCount > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={`Show ${overflowCount} additional artifacts`}
                  className="inline-flex flex-shrink-0 items-center gap-1 border-l border-midas-stone bg-midas-warm-stone/40 px-3 font-mono text-[11px] font-semibold uppercase tracking-[0.04em] text-midas-graphite transition-colors hover:bg-midas-warm-stone"
                >
                  <span>+{overflowCount}</span>
                  <ChevronDown size={12} strokeWidth={2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[260px] border-midas-stone bg-midas-bone-light p-1 font-sora text-[12px] text-midas-graphite shadow-lg"
              >
                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.08em] text-midas-muted-stone">
                  {`Open Artifacts · ${visibleTabs.length} in strip · ${overflowCount} overflow`}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-midas-stone" />
                {[...visibleTabs, ...overflowTabs].map((tab) => (
                  <DropdownMenuItem
                    key={tab.id}
                    onSelect={() => handleActivate(tab.id)}
                    className="flex items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-[12px] data-[highlighted]:bg-midas-warm-stone data-[highlighted]:text-midas-graphite"
                  >
                    <span className="truncate">{tab.artifact.title}</span>
                    {tab.id === activeTabId && (
                      <span className="font-mono text-[9px] uppercase tracking-[0.08em] text-midas-terracotta">
                        Active
                      </span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Body — reuses the desktop body component */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <MidasArtifactBodyV2 padding={0} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MidasPhoneArtifactScreenV2;
